import React, { useRef, useState } from 'react';

interface ImageUploadProps {
  currentUrl: string | null;
  baseUrl: string;
  uploadFn: (file: File) => Promise<{ data?: { logo_url?: string } }>;
  onSuccess: (fileName: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ currentUrl, baseUrl, uploadFn, onSuccess }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true);

    try {
      const res: any = await uploadFn(file);
      onSuccess(res?.data?.logo_url ?? '');
    } catch {
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const toAbsolute = (fileName: string | null): string | undefined => {
    if (!fileName) return undefined;
    return fileName.startsWith('http') ? fileName : `${baseUrl}/${fileName}`;
  };

  const displaySrc = preview ?? toAbsolute(currentUrl);

  return (
    <div
      className="position-relative d-inline-block cursor-pointer"
      onClick={() => !uploading && inputRef.current?.click()}
      title="Click to upload image"
    >
      <div
        className="d-flex align-items-center justify-content-center border rounded"
        style={{ width: 80, height: 80, overflow: 'hidden', background: 'var(--bs-body-bg)' }}
      >
        {displaySrc ? (
          <img src={displaySrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <i className="ri-image-add-line ri-24px" />
        )}
      </div>
      <div
        className="position-absolute bottom-0 end-0 d-flex align-items-center justify-content-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--bs-primary)',
          color: '#fff',
          fontSize: 11,
        }}
      >
        {uploading ? (
          <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} />
        ) : (
          <i className="ri-camera-line" />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="d-none"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ImageUpload;
